import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { Card } from "../../components/UI";
import { WizardButtons } from "./FormControls";
import { EMPTY_INTAKE } from "./intakeConstants";
import {
  InsuranceSection, ReferralSourceSection, PresentingProblemSection,
  DiagnosisSection, MedicationSection, HousingSection, RequestedServicesSection,
} from "./Sections";
import {
  IntakeDemographics, EmployerSchool, ExternalEntities, EmergencySection,
  PermissionToCommunicate, CallPortal, AuthorizedPrograms,
} from "./IntakeSections";
import { ArrowLeft, Eye } from "lucide-react";
import { toast } from "sonner";
import IntakePdfView from "./IntakePdfView";
import { WizardStepper } from "./WizardStepper";

const TABS = [
  { key: "demographics", label: "Demographics" },
  { key: "referral_source", label: "Referral Source" },
  { key: "insurance", label: "Insurance" },
  { key: "requested_services", label: "Requested Services" },
  { key: "presenting_problem", label: "Presenting Problem" },
  { key: "diagnosis", label: "Diagnosis" },
  { key: "medication", label: "Medication" },
  { key: "housing", label: "Housing/Placement" },
  { key: "employer_school", label: "Employer/School" },
  { key: "external_entities", label: "External Entities" },
  { key: "emergency", label: "Emergency" },
  { key: "permission_to_communicate", label: "Permission to Communicate" },
  { key: "call_portal", label: "Call/Text/Email Portal" },
  { key: "authorized_programs", label: "Authorized Programs/Teams" },
];

export default function IntakeWizard() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const stepParam = search.get("step");
  const clientIdParam = search.get("client_id");
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_INTAKE);
  const initialTabIdx = (() => {
    if (!stepParam) return 0;
    const i = TABS.findIndex((t) => t.key === stepParam);
    return i >= 0 ? i : 0;
  })();
  const [tab, setTab] = useState(initialTabIdx);
  const [visited, setVisited] = useState(() => new Set([initialTabIdx]));
  useEffect(() => { setVisited((v) => new Set(v).add(tab)); }, [tab]);
  const [companies, setCompanies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [pdfOpen, setPdfOpen] = useState(false);

  useEffect(() => {
    api.get("/insurance-companies").then((r) => setCompanies(r.data));
    if (id) {
      api.get(`/intake-forms/${id}`).then((r) => {
        const merged = { ...EMPTY_INTAKE, ...r.data };
        Object.keys(EMPTY_INTAKE).forEach((k) => {
          if (typeof EMPTY_INTAKE[k] === "object" && !Array.isArray(EMPTY_INTAKE[k])) {
            merged[k] = { ...EMPTY_INTAKE[k], ...(r.data[k] || {}) };
          }
        });
        setForm(merged); setLoading(false);
      }).catch(() => { toast.error("Intake form not found"); navigate("/engagement/intake"); });
    } else if (clientIdParam) {
      // Prefill demographics from an existing client so "Start Intake" from the
      // client console lands with the client's first/last name already filled in.
      api.get(`/clients/${clientIdParam}`).then((r) => {
        const c = r.data || {};
        setForm((f) => ({
          ...f,
          demographics: {
            ...f.demographics,
            first_name: c.first_name || "",
            last_name: c.last_name || "",
            dob: c.dob || "",
            gender: c.gender || "",
            phone: c.phone || "",
            email: c.email || "",
          },
          _linked_client_id: clientIdParam,
        }));
      }).catch(() => {});
    }
  }, [id, clientIdParam]);

  const setSection = (key) => (patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  const save = async () => {
    if (!form.demographics.first_name || !form.demographics.last_name) {
      toast.error("First and last name are required (Demographics tab)");
      setTab(0);
      return;
    }
    setBusy(true);
    try {
      if (id) await api.put(`/intake-forms/${id}`, form);
      else await api.post("/intake-forms", form);
      toast.success(id ? "Intake updated" : "Client intake saved - client record created");
      navigate("/engagement/intake");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const isLast = tab === TABS.length - 1;
  const next = () => (isLast ? save() : setTab(tab + 1));
  const back = () => (tab === 0 ? navigate("/engagement/intake") : setTab(tab - 1));

  if (loading) return <div className="py-20 text-center text-[#84675B]">Loading intake…</div>;
  const key = TABS[tab].key;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <button data-testid="intake-back-btn" onClick={() => navigate("/engagement/intake")} className="flex items-center gap-1 text-sm text-[#7B6157] hover:underline">
          <ArrowLeft size={15} /> Back
        </button>
        <button type="button" data-testid="see-pdf-view-btn" onClick={() => setPdfOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-[#D7CCC8] bg-white px-4 py-2 text-sm font-semibold text-[#4E342E] hover:bg-[#F5F2EB] transition-colors">
          <Eye size={14} /> See PDF View
        </button>
      </div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <h1 className="font-heading text-xl font-bold text-[#3E2723]">Intake Form</h1>
          <span className="text-sm text-[#84675B]">/ {id ? "Edit Client" : "Add New Client"}</span>
        </div>
        {(form.demographics.first_name || form.demographics.last_name) && (
          <div className="flex items-center gap-2" data-testid="intake-client-chip">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#8D6E63] text-xs font-bold text-white">
              {form.demographics.first_name?.[0]}{form.demographics.last_name?.[0]}
            </span>
            <span className="font-heading font-bold text-[#3E2723]">{form.demographics.first_name} {form.demographics.last_name}</span>
            {form.account_number && <span className="text-sm text-[#84675B]">#{form.account_number}</span>}
          </div>
        )}
      </div>
      <div className="mb-5">
        <WizardStepper tabs={TABS} active={tab} visited={visited} onSelect={setTab} testPrefix="intake-tab" />
      </div>

      <Card className="p-6 lg:p-8" data-testid={`intake-section-${key}`}>
        {key === "demographics" && <IntakeDemographics data={form.demographics} set={setSection("demographics")} />}
        {key === "referral_source" && <ReferralSourceSection data={form.referral_source} set={setSection("referral_source")} />}
        {key === "insurance" && <InsuranceSection data={form.insurance} set={setSection("insurance")} companies={companies} />}
        {key === "requested_services" && <RequestedServicesSection data={form.requested_services} set={setSection("requested_services")} />}
        {key === "presenting_problem" && <PresentingProblemSection data={form.presenting_problem} set={setSection("presenting_problem")} />}
        {key === "diagnosis" && <DiagnosisSection data={form.diagnosis} set={setSection("diagnosis")} />}
        {key === "medication" && <MedicationSection data={form.medication} set={setSection("medication")} />}
        {key === "housing" && <HousingSection data={form.housing} set={setSection("housing")} />}
        {key === "employer_school" && <EmployerSchool data={form.employer_school} set={setSection("employer_school")} />}
        {key === "external_entities" && <ExternalEntities data={form.external_entities} set={setSection("external_entities")} />}
        {key === "emergency" && <EmergencySection data={form.emergency} set={setSection("emergency")} />}
        {key === "permission_to_communicate" && <PermissionToCommunicate data={form.permission_to_communicate} set={setSection("permission_to_communicate")} />}
        {key === "call_portal" && <CallPortal data={form.call_portal} set={setSection("call_portal")} />}
        {key === "authorized_programs" && <AuthorizedPrograms data={form.authorized_programs} set={setSection("authorized_programs")} />}
        <WizardButtons onBack={back} onNext={next} nextLabel={isLast ? "Save & Continue" : "Next"} busy={busy} />
      </Card>
      {pdfOpen && <IntakePdfView form={form} tabs={TABS} onClose={() => setPdfOpen(false)} />}
    </div>
  );
}
